/**
 * handel-magazyn.ts — DTO modułu „Handel i Magazyn": kształt, który widzi przeglądarka.
 *
 * To NIE jest kontrakt Symfonii. Surowe typy WebAPI żyją w
 * `apps/api/lib/handel-magazyn/hmf-types.ts` i nie opuszczają backendu — razem z pułapkami
 * (pola enumeracyjne jako liczby, waluta PLN jako pusty string, liczbowe flagi). Mapper jest
 * jedynym miejscem, w którym te dziwactwa są tłumaczone; dzięki temu front operuje na danych,
 * które znaczą to, co mówią.
 *
 * Kwoty jako `string`, nie `number` — konsekwentnie z resztą repo (kwoty dokumentów trzymamy
 * jako tekst dziesiętny, żeby nie wprowadzać liczb zmiennoprzecinkowych w pieniądz).
 */

/** Pozycja listy dokumentów sprzedaży. */
export interface HmSaleListItem {
  id: number;
  documentNumber: string;
  /** `true` = dokument jeszcze w buforze (niezatwierdzony w Symfonii). */
  inBuffer: boolean;
  canceled: boolean;
  settled: boolean;
  issueDate: string | null;
  saleDate: string | null;
  maturityDate: string | null;
  typeCode: string;
  series: string;
  /**
   * Nazwa nabywcy. Dla dokumentu z kartoteki rozwiązana ze słownika kontrahentów, dla nabywcy
   * JEDNOKROTNEGO wzięta z danych adresowych dokumentu — Symfonia nie nadaje mu identyfikatora,
   * więc słownik nic by tu nie powiedział. `null` = nie udało się ustalić.
   */
  buyerName: string | null;
  buyerId: number | null;
  /**
   * `true` = nabywca wpisany wprost na dokumencie, bez kartoteki („kontrahent incydentalny").
   * Rozpoznawane po braku identyfikatora przy obecnej nazwie w danych adresowych. Na LIŚCIE
   * wypełnione tylko po wzbogaceniu ze szczegółów — sama lista nie niesie danych adresowych.
   */
  buyerIsAdHoc: boolean;
  netValue: string;
  vatValue: string;
  grossValue: string;
  /** Zawsze konkretna waluta — pusty string z Symfonii jest tłumaczony na `PLN`. */
  currency: string;
  description: string;
  /** Tryb KSeF dokumentu — jedyne pole KSeF, które Symfonia podaje na liście. */
  invoiceKind: HmInvoiceKind;
  /**
   * Pola KSeF pochodzą ze SZCZEGÓŁÓW dokumentu, więc na liście pojawiają się tylko wtedy, gdy
   * poprosisz o wzbogacenie (`withKsef`). `null` znaczy „nie pobrano albo dokument jest poza
   * KSeF" — rozróżnia je `invoiceKind` i `ksefLoaded` w kopercie odpowiedzi.
   */
  ksefStatusCode: number | null;
  ksefNumber: string | null;
  ksefIssueDate: string | null;
  /**
   * Stan fiskalizacji wg Symfonii. Jak pola KSeF pochodzi ze SZCZEGÓŁÓW dokumentu — na samej
   * liście Symfonia go nie podaje (sprawdzone na żywo: wiersz listy ma 29 pól i żadnego
   * fiskalnego), więc przyjeżdża tym samym wzbogaceniem co KSeF.
   *
   * TRZY STANY, nie dwa: `null` znaczy „nie pobrano" i NIE wolno go rysować jako
   * „niezafiskalizowany" — brak znacznika przy dokumencie, który paragon ma, wygląda jak
   * zaproszenie do wydrukowania go drugi raz.
   */
  fiscalized: boolean | null;
}

/**
 * Tryb KSeF dokumentu (wartości ze swaggera Symfonii). `UNKNOWN` to nasza wartość zapasowa na
 * wypadek trybu, którego jeszcze nie widzieliśmy — mapper nigdy nie rzuca na nieznanej wartości.
 */
export type HmInvoiceKind = 'NoInvoice' | 'ForcedPaper' | 'EInvoice' | 'Structured' | 'UNKNOWN';

export interface HmDocumentAddress {
  name: string | null;
  taxId: string | null;
  city: string | null;
  postCode: string | null;
  street: string | null;
  houseNo: string | null;
  apartmentNo: string | null;
}

export interface HmDocumentPosition {
  id: number;
  no: number;
  /**
   * Identyfikator towaru w Symfonii. Potrzebny, żeby dociągnąć nazwę fiskalną (`FiscalName`),
   * której pozycja dokumentu NIE niesie — na paragonie ma iść ona, nie nazwa handlowa.
   */
  productId: number | null;
  productCode: string;
  description: string;
  quantity: string;
  unit: string;
  /**
   * Identyfikator stawki ze słownika Symfonii. To jest KLUCZ przy mapowaniu stawki na literę
   * drukarki fiskalnej — `vatRateCode` się do tego nie nadaje, bo ten sam kod przychodzi raz
   * z wiodącą spacją (" 23%"), raz bez (udokumentowana pułapka w `hmf-types.ts`).
   */
  vatRateId: number | null;
  vatRateCode: string | null;
  priceValue: string;
  netValue: string;
  vatValue: string;
  grossValue: string;
}

export interface HmSaleDetail extends HmSaleListItem {
  positions: HmDocumentPosition[];
  /**
   * `true` = dokument odnotowany jako zafiskalizowany. W szczegółach stan jest ZAWSZE znany,
   * więc tutaj — w odróżnieniu od wiersza listy — nie ma trzeciego stanu „nie pobrano".
   */
  fiscalized: boolean;
  buyerAddress: HmDocumentAddress | null;
  recipientAddress: HmDocumentAddress | null;
  note: string | null;
  /**
   * Forma płatności ze słownika Symfonii. Drukarka fiskalna nie zna „form", tylko TYPY
   * (gotówka / karta / przelew), więc przy stanowisku żyje mapa identyfikator → typ.
   */
  paymentFormId: number | null;
  /**
   * Rodzaj ceny na dokumencie: 0 nieokreślony, 1 BRUTTO, 2 NETTO (ustalone eksperymentalnie —
   * swagger tego nie podaje). Do druku paragonu NIE liczymy z tego pola (źródłem prawdy jest
   * wartość brutto pozycji), ale przy rozjeździe grosza mówi ono, na czym liczyła Symfonia.
   */
  priceKind: number | null;
}

/** Koperta listy — niesie też informację, czy wynik został przycięty. */
export interface HmListResponse<T> {
  items: T[];
  /** `true` = pokazano tylko początek; UI prosi o węższy zakres dat. */
  truncated: boolean;
  fetchedAt: string;
  /** Nazwa połączenia, z którego pochodzą dane — przy kilku integracjach jest istotna. */
  integrationName: string;
  /**
   * Tylko lista sprzedaży: czy pola KSeF zostały dociągnięte ze szczegółów dokumentów.
   * `ksefSkipped` = zakres był za duży, żeby to zrobić bez zamrożenia ekranu — UI prosi wtedy
   * o węższy zakres zamiast pokazywać puste kolumny bez wyjaśnienia.
   */
  ksefLoaded?: boolean;
  ksefSkipped?: boolean;
}

/** Powody, dla których moduł nie ma skąd czytać danych — front mapuje je na konkretną radę. */
export type HmIntegrationProblem =
  | 'NO_INTEGRATION'
  | 'INTEGRATION_DISABLED'
  | 'AGENT_MODE_UNSUPPORTED'
  | 'APPLICATION_GUID_NOT_SET'
  | 'AMBIGUOUS_INTEGRATION'
  | 'SYMFONIA_UNAVAILABLE'
  | 'SYMFONIA_TIMEOUT';

/** Pozycja listy dokumentów magazynowych. */
export interface HmWarehouseListItem {
  id: number;
  documentNumber: string;
  inBuffer: boolean;
  canceled: boolean;
  /** `false` = dokument nierozliczony (np. WZ, do którego nie ma jeszcze faktury). */
  settled: boolean;
  issueDate: string | null;
  operationDate: string | null;
  typeCode: string;
  series: string;
  contractorName: string | null;
  warehouseName: string | null;
  /** Magazyn docelowy — tylko dla przesunięć międzymagazynowych. */
  receivingWarehouseName: string | null;
  netValue: string;
  documentValue: string;
  currency: string;
  description: string;
}

/**
 * Dostawa (partia) powiązana z pozycją dokumentu magazynowego: na rozchodzie mówi, z której
 * partii towar zszedł, na przychodzie — którą partię utworzył.
 *
 * `code` to albo numer dokumentu przyjęcia („0011/26/PZ"), albo kod partii nadany przy
 * przyjęciu („059056686400648621") — pokazujemy go dosłownie, bez interpretowania.
 */
export interface HmPositionDelivery {
  id: number;
  code: string;
  date: string | null;
  /** Ile z TEJ pozycji zeszło z tej dostawy — suma po dostawach daje ilość pozycji. */
  quantity: string;
}

/** Pozycja dokumentu magazynowego — bez brutto i VAT (Symfonia ich tu nie podaje). */
export interface HmWarehousePosition {
  id: number;
  no: number;
  productCode: string;
  description: string;
  quantity: string;
  unit: string;
  priceValue: string;
  netValue: string;
  /**
   * PUSTA dla dokumentów w buforze i dla pozycji usługowych — Symfonia przypisuje partie dopiero
   * przy zatwierdzeniu dokumentu. Brak dostaw to informacja, nie błąd.
   */
  deliveries: HmPositionDelivery[];
}

export interface HmWarehouseDetail extends HmWarehouseListItem {
  positions: HmWarehousePosition[];
  note: string | null;
}

/** Pozycja listy zamówień (ZMO — obce, ZWO — własne). */
export interface HmOrderListItem {
  id: number;
  documentNumber: string;
  inBuffer: boolean;
  canceled: boolean;
  settled: boolean;
  issueDate: string | null;
  saleDate: string | null;
  maturityDate: string | null;
  typeCode: string;
  series: string;
  buyerName: string | null;
  netValue: string;
  vatValue: string;
  grossValue: string;
  currency: string;
  description: string;
}

export interface HmOrderDetail extends HmOrderListItem {
  positions: HmDocumentPosition[];
  buyerAddress: HmDocumentAddress | null;
  note: string | null;
}

/** Rozliczenie na dokumencie kasowym — do czego przypisano wpłatę/wypłatę. */
export interface HmPaymentSettlement {
  id: number;
  settledDocumentNumber: string | null;
  value: string;
}

/**
 * Pozycja listy kasy i banku. `typeCode` jest `null` NA LIŚCIE — Symfonia podaje go dopiero
 * w szczegółach dokumentu; kolumna pokazuje wtedy kreskę zamiast udawać dane.
 */
export interface HmPaymentListItem {
  id: number;
  documentNumber: string;
  typeCode: string | null;
  /** Etykieta rodzaju operacji wyprowadzona z liczbowego kodu Symfonii. */
  paymentTypeLabel: string;
  subjectTypeLabel: string;
  issueDate: string | null;
  maturityDate: string | null;
  settlementDate: string | null;
  registryName: string | null;
  subjectName: string | null;
  totalValue: string;
  settledValue: string;
  currency: string;
}

export interface HmPaymentDetail extends HmPaymentListItem {
  settlements: HmPaymentSettlement[];
  canceled: boolean;
  description: string | null;
}

/**
 * Cena towaru w jednym cenniku. `priceType` odpowiada `HmSalePriceType.type` („typ A" = 1…),
 * a `kind` mówi, czy TA cena jest brutto czy netto (skala `HM_PRICE_KINDS`) — w obrębie jednego
 * towaru różne cenniki mogą mieć różny rodzaj.
 */
export interface HmProductPrice {
  priceType: number;
  kind: number;
  value: string;
  currency: string;
}

/** Towar z kartoteki Symfonii (nie mylić z lokalną kartoteką Towary). */
export interface HmProductListItem {
  id: number;
  code: string;
  name: string;
  active: boolean;
  unit: string | null;
  vatRate: string | null;
  typeLabel: string;
  barcode: string | null;
  cn: string | null;
  pkwiu: string | null;
  /**
   * Ceny z cenników. PUSTA, dopóki lista nie zostanie o nie poproszona (`withPrices`) —
   * kartoteka towarów ich nie potrzebuje, a podwajają odpowiedź.
   */
  prices: HmProductPrice[];
}

// ── Wystawianie dokumentu sprzedaży (Fala 2) ────────────────────────────────

/** Pozycja słownika o kształcie „identyfikator + etykieta" — typy, serie, formy, działy. */
export interface HmDictionaryItem {
  id: number;
  code: string;
  name: string;
}

/** Typ dokumentu sprzedaży wraz z charakterem (FVS, PAR, WDT…). */
export interface HmSaleDocumentType extends HmDictionaryItem {
  character: string;
}

/** Dział firmy — Symfonia identyfikuje go KODEM, nie numerem, przy wystawianiu. */
export type HmDepartment = HmDictionaryItem;

export interface HmPaymentForm {
  id: number;
  name: string;
  /** Liczba dni do terminu płatności (Symfonia wylicza z niej termin). */
  term: number | null;
}

export interface HmPaymentRegistryItem {
  id: number;
  name: string;
  code: string | null;
  defaultPaymentFormId: number | null;
}

export interface HmVatRate {
  id: number;
  /** Kod przycięty z białych znaków — słownik Symfonii bywa z wiodącą spacją (" 23%"). */
  code: string;
  /** Procent jako liczba, gdy dało się go odczytać z kodu — do podglądowego wyliczenia kwot. */
  percent: number | null;
}

export interface HmSalePriceType {
  type: number;
  /** Etykieta do pokazania — Symfonia trzyma ją w `Code`, bo `Name` bywa puste. */
  name: string;
  /** Rodzaj ceny cennika w skali `HM_PRICE_KINDS` — podpowiedź dla ustawienia dokumentu. */
  kind: number;
}

/**
 * Rodzaj ceny na dokumencie: czy `Value` pozycji jest kwotą BRUTTO czy NETTO.
 * Wartości są kontraktem Symfonii (nie ma ich w swaggerze — ustalone na żywym dokumencie).
 * Pomyłka tutaj nie daje błędu, tylko dokument na inną kwotę.
 */
export const HM_PRICE_KINDS = {
  GROSS: 1,
  NET: 2,
} as const;

/** Słowniki potrzebne do zbudowania nowego dokumentu sprzedaży — jedno zapytanie, jedna sesja. */
export interface HmSaleDictionaries {
  documentTypes: HmSaleDocumentType[];
  departments: HmDepartment[];
  paymentRegistries: HmPaymentRegistryItem[];
  vatRates: HmVatRate[];
  salePriceTypes: HmSalePriceType[];
}

/** Kontrahent z kartoteki Symfonii — do wskazania nabywcy na dokumencie. */
/**
 * Kontrahent z kartoteki SYMFONII — nie mylić z naszym kontrahentem z bazy tenanta.
 *
 * Te dwa zbiory świadomie się nie mieszają: dokument powstaje w Symfonii i wskazuje kontrahenta
 * z JEJ kartoteki, a nasz identyfikator nic by tam nie znaczył. Kartoteka po naszej stronie jest
 * wyłącznie podglądem — nie synchronizujemy, nie kopiujemy, nie edytujemy.
 *
 * PESEL nie występuje w tym typie CELOWO, choć Symfonia go oddaje (decyzja właściciela
 * 2026-09-14) — do podglądu handlowego nie jest potrzebny, a danych, których nie przenosimy,
 * nie trzeba chronić.
 */
export interface HmContractorListItem {
  id: number;
  code: string;
  name: string;
  taxId: string | null;
  active: boolean;
  /** Miejscowość i kod pocztowy są już NA LIŚCIE — pozwalają odróżnić kontrahentów o tej samej nazwie. */
  city: string | null;
  postCode: string | null;
}

/** Adres kontrahenta — ten sam kształt dla domyślnego, korespondencyjnego i adresów dostaw. */
export interface HmContractorAddress {
  country: string | null;
  city: string | null;
  province: string | null;
  street: string | null;
  houseNo: string | null;
  apartmentNo: string | null;
  postCode: string | null;
  fullAddress: string | null;
}

/** Rozrachunki kontrahenta — kwoty tekstem, bo to pieniądze (patrz reszta modułu). */
export interface HmContractorBalance {
  /** Należności — ile kontrahent jest nam winien. */
  due: string;
  /** Zobowiązania — ile my jesteśmy winni kontrahentowi. */
  obligation: string;
}

export interface HmContractorDetail extends HmContractorListItem {
  regon: string | null;
  /** Zarejestrowany w VIES (transakcje wewnątrzwspólnotowe). */
  vies: boolean;
  vatTaxPayer: boolean;
  creditLimit: boolean;
  maxCreditValue: string | null;
  creditCurrency: string | null;
  priceNegotiation: boolean;
  defaultDiscountPercent: string | null;
  note: string | null;
  balance: HmContractorBalance;
  contact: {
    name: string | null;
    phone1: string | null;
    phone2: string | null;
    email: string | null;
    www: string | null;
  };
  defaultAddress: HmContractorAddress | null;
  correspondenceAddress: HmContractorAddress | null;
  deliveryAddresses: HmContractorAddress[];
  bank: { accountNumber: string | null; bankName: string | null; swift: string | null };
  kindCode: string | null;
  catalogName: string | null;
}

/**
 * Wariant KSeF przy wystawianiu. Wartości są kontraktem Symfonii, nie naszym wymysłem.
 * `NONE` (-1) to jedyny wariant, który NIE wywołuje skutków poza Symfonią.
 */
export const HM_KSEF_KINDS = {
  NONE: -1,
  PAPER: 0,
  STRUCTURED_MANUAL: 2,
  STRUCTURED_AUTO: 3,
} as const;

export type HmKsefKind = (typeof HM_KSEF_KINDS)[keyof typeof HM_KSEF_KINDS];

/**
 * Pozycja nowego dokumentu tak, jak myśli o niej użytkownik: ILOŚĆ i CENA JEDNOSTKOWA.
 * Symfonia przyjmuje wartość pozycji — przeliczenie robi backend, żeby nie rozjechało się
 * między formularzem a innym klientem tego API.
 */
export interface HmSaleIssuePositionInput {
  productCode: string;
  quantity: number;
  unitPrice: number;
  unitOfMeasurement?: string;
  name?: string;
  vatRateId?: number;
}

/**
 * Nabywca wpisany wprost na dokumencie, bez zapisu do kartoteki Symfonii — odpowiednik
 * „kontrahenta incydentalnego" z okna Symfonii. Nazwa jest jedynym polem wymaganym; resztę
 * Symfonia przyjmie pustą (faktura dla osoby fizycznej nie musi mieć NIP-u).
 */
export interface HmAdHocBuyer {
  name: string;
  taxId?: string;
  country?: string;
  city?: string;
  street?: string;
  houseNo?: string;
  apartmentNo?: string;
  postCode?: string;
}

/** Żądanie wystawienia dokumentu sprzedaży wysyłane z formularza. */
export interface HmSaleIssueInput {
  documentTypeCode: string;
  series: string;
  departmentCode: string;
  paymentRegistryId: number;
  paymentFormId: number;
  salePriceType?: number;
  priceKind?: number;
  issueDate?: string;
  saleDate?: string;
  maturityDate?: string;
  /** Nabywca z kartoteki Symfonii. Wyklucza się z `buyerAdHoc`. */
  buyerId?: number;
  /** Nabywca jednokrotny (bez zapisu do kartoteki). Wyklucza się z `buyerId`. */
  buyerAdHoc?: HmAdHocBuyer;
  description?: string;
  note?: string;
  receivedBy?: string;
  splitPayment?: boolean;
  positions: HmSaleIssuePositionInput[];
  /** `false` = zapis do bufora, `true` = zatwierdzenie z nadaniem numeru (nieodwracalne). */
  issue: boolean;
  ksefKind: HmKsefKind;
}

/**
 * Odnotowanie fiskalizacji. NIE DRUKUJE paragonu — zapisuje numer paragonu wydrukowanego na
 * fizycznej drukarce przy dokumencie w Symfonii.
 */
export interface HmFiscalizeInput {
  receiptNumber: number;
  fiscalDeviceNo?: string;
}

/** Odpowiedź po wystawieniu — numer nadaje Symfonia, my go tylko pokazujemy. */
export interface HmSaleIssueResult {
  id: number;
  documentNumber: string;
  inBuffer: boolean;
  netValue: string;
  vatValue: string;
  grossValue: string;
  currency: string;
}

// ── Wystawianie dokumentów kasowych (kasa i bank) ───────────────────────────

/**
 * Rodzaje dokumentów płatniczych. Wpłaty i wypłaty (KP/KW/BP/BW) mogą wskazywać kontrahenta;
 * „inne przyjęcie/wydanie" (IP/IW) to operacje kasowe bez kontrahenta; transfery (TR±)
 * przesuwają środki między rejestrami i mają rejestr docelowy zamiast podmiotu.
 */
export const HM_PAYMENT_KINDS = ['KP', 'KW', 'BP', 'BW', 'IP', 'IW', 'TRMinus'] as const;

export type HmPaymentKind = (typeof HM_PAYMENT_KINDS)[number];

/**
 * Czy dany rodzaj MOŻE wskazywać kontrahenta (i rozliczać jego rozrachunki).
 *
 * Kontrahent jest tu OPCJONALNY, nie wymagany — decyzja właściciela z 2026-09-14. Wcześniej
 * wymuszaliśmy go przy KP/KW/BP/BW, ale to była NASZA reguła „sensu księgowego", nie kontrakt
 * Symfonii: wpłata do kasy potrafi nie mieć kontrahenta (zasilenie kasy, wpłata własna) i
 * blokowanie jej zmuszało do wskazywania przypadkowego podmiotu, czyli do wpisywania nieprawdy.
 * O tym, czy dokument bez podmiotu jest dopuszczalny, rozstrzyga teraz Symfonia — a jej odmowę
 * pokazujemy dosłownie.
 *
 * `false` znaczy „pole nie ma tu sensu": IP/IW są z definicji bezpodmiotowe, a przy transferze
 * podmiotem jest rejestr docelowy.
 */
export function hmPaymentSupportsSubject(kind: HmPaymentKind): boolean {
  return kind === 'KP' || kind === 'KW' || kind === 'BP' || kind === 'BW';
}

/** Czy dany rodzaj to transfer między rejestrami (rejestr docelowy zamiast kontrahenta). */
export function hmPaymentIsTransfer(kind: HmPaymentKind): boolean {
  return kind === 'TRMinus';
}

/** Nierozliczony rozrachunek — z nich buduje się listę rozliczeń dokumentu kasowego. */
export interface HmSettlementListItem {
  id: number;
  documentId: number;
  documentNumber: string;
  contractorId: number | null;
  issueDate: string | null;
  maturityDate: string | null;
  totalValue: string;
  paidValue: string;
  leftToPayValue: string;
  currency: string;
}

/** Rozliczenie wskazane na wystawianym dokumencie kasowym. */
export interface HmPaymentSettlementInput {
  settlementId: number;
  documentId: number;
  documentNumber: string;
  value: number;
  settlementDate?: string;
}

/** Żądanie wystawienia dokumentu kasowego wysyłane z formularza. */
export interface HmPaymentIssueInput {
  kind: HmPaymentKind;
  documentTypeCode: string;
  series: string;
  paymentRegistryId: number;
  /** Kontrahent — wymagany dla KP/KW/BP/BW, zabroniony przy transferze. */
  subjectId?: number;
  /** Rejestr docelowy transferu — wymagany przy TR−, zabroniony przy pozostałych. */
  targetPaymentRegistryId?: number;
  /** Kwota dokumentu. Pusta przy rozliczeniach = Symfonia wyliczy ją z ich sumy. */
  totalValue?: number;
  paymentDate?: string;
  note?: string;
  currency?: string;
  currencyRate?: number;
  settlements?: HmPaymentSettlementInput[];
}

/** Słowniki potrzebne do wystawienia dokumentu kasowego danego rodzaju. */
export interface HmPaymentDictionaries {
  documentTypes: HmDictionaryItem[];
  paymentRegistries: HmPaymentRegistryItem[];
}

/** Odpowiedź po wystawieniu dokumentu kasowego. */
export interface HmPaymentIssueResult {
  id: number;
  documentNumber: string;
  totalValue: string;
  settledValue: string;
  currency: string;
}

// ── Wystawianie i edycja dokumentów magazynowych ────────────────────────────

/** Charaktery dokumentów magazynowych dostępne w module. */
export const HM_WAREHOUSE_KINDS = ['PZ', 'PW', 'WZ', 'RW', 'MMMinus'] as const;

export type HmWarehouseKind = (typeof HM_WAREHOUSE_KINDS)[number];

/**
 * Pozycja dokumentu magazynowego w formularzu.
 *
 * `positionId` i `deleted` obsługują EDYCJĘ: Symfonia oczekuje pełnej listy pozycji, w której
 * istniejące mają identyfikator, nowe go nie mają, a usunięte są oznaczone. Pominięcie pozycji
 * NIE usuwa jej z dokumentu — dlatego formularz musi odesłać także te skreślone.
 */
export interface HmWarehousePositionInput {
  positionId?: number;
  productCode: string;
  quantity: number;
  unitOfMeasurement?: string;
  name?: string;
  /** Wartość pozycji (nie cena jednostkowa) — jak przy sprzedaży. */
  value?: number;
  deleted?: boolean;
}

/** Żądanie wystawienia lub edycji dokumentu magazynowego. */
export interface HmWarehouseIssueInput {
  kind: HmWarehouseKind;
  documentTypeCode: string;
  series: string;
  /** Magazyn źródłowy — Symfonia identyfikuje go kodem. */
  warehouseCode?: string;
  warehouseRegistryCode?: string;
  /** Magazyn przyjmujący — wymagany przy przesunięciu, zabroniony przy pozostałych. */
  receivingWarehouseCode?: string;
  contractorId?: number;
  issueDate?: string;
  operationDate?: string;
  description?: string;
  note?: string;
  positions: HmWarehousePositionInput[];
  /** `false` = zapis do bufora, `true` = zatwierdzenie. Przy edycji pomijane. */
  issue?: boolean;
  /**
   * Przy przesunięciu (MM−): wystaw od razu odpowiadający mu dokument MM+ w magazynie
   * przyjmującym. Wymaga ZATWIERDZENIA dokumentu MM− — do dokumentu w buforze Symfonia MM+
   * nie wystawi, bo przesunięcie jeszcze się nie wydarzyło.
   */
  withReceivingDocument?: boolean;
}

/** Słowniki do wystawienia dokumentu magazynowego danego charakteru. */
export interface HmWarehouseDictionaries {
  documentTypes: HmDictionaryItem[];
  warehouses: HmDictionaryItem[];
  warehouseRegistries: HmDictionaryItem[];
}

/**
 * Szablon wydruku z Konfiguratora Handlu — odpowiednik listy „Nazwa raportu" w oknie Symfonii
 * „Wybór wydruku dokumentu". Oddajemy TYLKO szablony okna dokumentu; kartotekowe (wydruki list)
 * Symfonia i tak odrzuca.
 */
export interface HmPrintTemplate {
  id: number;
  name: string;
}

/**
 * Nagłówek wydruku — to samo pole, którym w Symfonii wybiera się oryginał, kopię lub duplikat.
 * `Empty` = bez nagłówka.
 */
export const HM_PRINT_COPY_KINDS = [
  'Empty',
  'Original',
  'Copy',
  'Original_Copy',
  'Duplicate',
] as const;

export type HmPrintCopyKind = (typeof HM_PRINT_COPY_KINDS)[number];

/** Ustawienia wydruku wybierane w oknie przed wydrukiem. */
export interface HmPrintSettings {
  /** `null` = wydruk domyślny Symfonii (bez wskazywania szablonu). */
  templateId: number | null;
  copyKind: HmPrintCopyKind;
  /** Drukuj treść uwag dokumentu — działa tylko dla wydruku domyślnego. */
  printNote: boolean;
  footer: string;
  issuedBy: string;
  receivedBy: string;
}

/** Para „wartość + waluta" z karty towaru. */
export interface HmProductMoney {
  value: string;
  currency: string;
}

/**
 * Szczegół towaru — wszystkie parametry karty, pogrupowane tak, jak człowiek ich szuka.
 * Wartości nieustawione oddajemy jako `null`, nie jako pusty string: „brak" ma wyglądać jak brak.
 */
export interface HmProductDetail {
  id: number;
  code: string;
  name: string;
  active: boolean;
  typeLabel: string;
  /** Surowy kod typu — do edycji i na wypadek typu, którego jeszcze nie widzieliśmy. */
  type: number;

  vatRateCode: string | null;
  kindCode: string | null;
  catalogPath: string | null;
  markerId: number;
  jpkV7Group: number;

  barcode: string | null;
  pkwiu: string | null;
  cn: string | null;
  cnFull: string | null;
  fiscalName: string | null;
  deliveryScheme: string | null;
  agriculturalPromotionFund: string | null;
  note: string | null;

  minState: string;
  maxState: string;

  priceNegotiation: boolean;
  vat50Minus: boolean;
  reverseCharge: boolean;
  splitPayment: boolean;
  staticSet: boolean;
  priceSetMode: boolean;
  collationSetMode: boolean;
  salePriceRecalculate: boolean;
  currencyMode: boolean;
  currencyRate: string;
  currencyConversion: number;
  /** `null`, gdy Symfonia oddała datę zerową (`0001-01-01`) — to jej zapis „brak daty". */
  currencyRateDate: string | null;

  recordUnit: string | null;
  defaultUnit: string | null;
  displayUnit: string | null;
  additionalUnit1: string | null;
  additionalUnit2: string | null;
  ratioOfAdditionalUnit1: string;
  ratioOfAdditionalUnit2: string;
  ratioOfDefaultUnit: string;

  basePrice: HmProductMoney | null;
  purchasePrice: HmProductMoney | null;
  purchasePriceInCurrency: HmProductMoney | null;
  prices: HmProductPrice[];

  barcodes: { barcode: string; unit: string | null }[];
  logisticFields: { label: string; value: string }[];
  fkId: string | null;
  fkParameter: string | null;
  intrastatWeightRatio: string | null;
  intrastatUnit: string | null;
}

/**
 * Zmiany karty towaru. Wysyłamy WYŁĄCZNIE to, co użytkownik ruszył — resztę karty trasa dokłada
 * z odczytu, bo Symfonia zapisuje CAŁY obiekt i pole pominięte w żądaniu zostałoby wyczyszczone.
 */
export interface HmProductUpdateInput {
  name?: string;
  active?: boolean;
  barcode?: string;
  pkwiu?: string;
  cn?: string;
  cnFull?: string;
  fiscalName?: string;
  deliveryScheme?: string;
  note?: string;
  vatRateCode?: string;
  minState?: number;
  maxState?: number;
  priceNegotiation?: boolean;
  vat50Minus?: boolean;
  reverseCharge?: boolean;
  splitPayment?: boolean;
}

/**
 * Nowy towar. Symfonia wymaga TRZECH rzeczy: kodu, nazwy i stawki VAT — sprawdzone odmowami na
 * żywej instancji (2026-09-13: „Nazwa towaru nie może być pusta", „Stawka VAT musi być określona").
 * Reszta pól jest opcjonalna i domyślnie zostaje pusta, tak jak przy zakładaniu towaru w Handlu.
 */
export interface HmProductCreateInput {
  code: string;
  name: string;
  vatRateCode: string;
  /** 0 = Towar, 1 = Usługa, 2 = Zestaw, 3 = Opakowanie. */
  type: number;
  unit?: string;
  kindCode?: string;
  pkwiu?: string;
  cn?: string;
  barcode?: string;
  fiscalName?: string;
  note?: string;
  minState?: number;
  maxState?: number;
  vat50Minus?: boolean;
  reverseCharge?: boolean;
  splitPayment?: boolean;
}

/** Słowniki potrzebne formularzowi nowego towaru. */
export interface HmProductDictionaries {
  vatRates: { id: number; code: string; description: string }[];
  kinds: { id: number; code: string }[];
}

/** Odpowiedź po wystawieniu lub edycji dokumentu magazynowego. */
export interface HmWarehouseIssueResult {
  /**
   * `true` = do tego przesunięcia wystawiono automatycznie dokument MM+.
   *
   * NUMERU zwykle nie znamy: trasa MM+ potwierdza wykonanie, ale oddaje PUSTĄ odpowiedź
   * (sprawdzone na żywo 2026-09-13 — powstało `0002/26/MM+`, a treść odpowiedzi była pusta).
   * Dlatego sukcesem jest brak odmowy, a nie obecność numeru; numer podajemy, gdy wyjątkowo
   * przyjdzie.
   */
  receivingIssued?: boolean;
  receivingDocument?: { id: number; documentNumber: string } | null;
  /** Treść odmowy Symfonii dla MM+, gdy MM− się udał, a MM+ nie. */
  receivingError?: string | null;
  id: number;
  documentNumber: string;
  inBuffer: boolean;
  netValue: string;
  documentValue: string;
  currency: string;
}
